import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';

interface SponsorCategory {
  id: string;
  name: string;
  display_order: number;
}

interface Sponsor {
  id: string;
  category_id: string | null;
  name: string;
  website_url: string | null;
  logo_url: string | null;
  display_order: number;
  is_active: boolean;
}

type GroupedSponsors = Record<string, { category: SponsorCategory; sponsors: Sponsor[] }>;

export default function SponsorsSection() {
  const [grouped, setGrouped] = useState<GroupedSponsors>({});
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [catRes, spRes] = await Promise.all([
        supabase.from('sponsor_categories').select('*').eq('is_active', true).order('display_order'),
        supabase.from('sponsors').select('*').eq('is_active', true).order('display_order'),
      ]);

      const categories = (catRes.data || []) as SponsorCategory[];
      const sponsors = (spRes.data || []) as Sponsor[];

      const groupedData: GroupedSponsors = {};
      categories.forEach((cat) => {
        groupedData[cat.id] = { category: cat, sponsors: [] };
      });

      sponsors.forEach((sp) => {
        const catId = sp.category_id || '__uncategorized';
        if (!groupedData[catId]) {
          groupedData[catId] = {
            category: { id: catId, name: 'Partners', display_order: 999 },
            sponsors: [],
          };
        }
        groupedData[catId].sponsors.push(sp);
      });

      setGrouped(groupedData);
    } catch {
      // silent, empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useRealtime<Sponsor>({ table: 'sponsors', onAny: () => loadData() });
  useRealtime<SponsorCategory>({ table: 'sponsor_categories', onAny: () => loadData() });

  const categoryEntries = Object.values(grouped).filter((g) => g.sponsors.length > 0);

  if (loading) {
    return (
      <section id="sponsors" className="py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold tracking-wide uppercase mb-3">Our Partners</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">Trusted by <span className="text-primary-600">Industry Leaders</span></h2>
          </div>
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
          </div>
        </div>
      </section>
    );
  }

  if (categoryEntries.length === 0) {
    return (
      <section id="sponsors" className="py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold tracking-wide uppercase mb-3">Our Partners</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">Trusted by <span className="text-primary-600">Industry Leaders</span></h2>
          </div>
          <div className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-building-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">Sponsors will be announced soon</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="sponsors" className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold tracking-wide uppercase mb-3">
            Our Partners
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
            Trusted by <span className="text-primary-600">Industry Leaders</span>
          </h2>
        </div>

        <div className="space-y-12">
          {categoryEntries.map(({ category, sponsors }) => (
            <div key={category.id}>
              <h3 className="text-center text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-6">{category.name}</h3>
              <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
                {sponsors.map((s) => (
                  <div key={s.id} className="bg-background-50 rounded-2xl px-6 py-4 border border-background-200/70">
                    {s.website_url ? (
                      <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                        {s.logo_url ? (
                          <img src={s.logo_url} alt={s.name} className="w-36 h-14 object-contain" />
                        ) : (
                          <div className="w-36 h-14 flex items-center justify-center">
                            <span className="font-heading font-bold text-lg text-foreground-400">{s.name}</span>
                          </div>
                        )}
                      </a>
                    ) : (
                      s.logo_url ? (
                        <img src={s.logo_url} alt={s.name} className="w-36 h-14 object-contain" />
                      ) : (
                        <div className="w-36 h-14 flex items-center justify-center">
                          <span className="font-heading font-bold text-lg text-foreground-400">{s.name}</span>
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}